import React, { useMemo } from "react";
import { Box, Button, IconButton, Tooltip, Skeleton } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_FilterFns,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
} from "material-react-table";
import { Download } from "lucide-react";
import ExportFilenamePopover from "./ExportFilenamePopover";

export default function CentralizedTable({
  columns,
  rows = [],
  getRowId,
  rowIdField = "id",
  enableExport = true,
  enableEditing = false,
  renderTopToolbarCustomActions,
  customInternalActions,
  pageSizeOptions = [10, 25, 50],
  initialState = {},
  isLoading = false,
  ...rest
}) {
  // Convert DataGrid GridColDef into MaterialReactTable columns
  const mrtColumns = useMemo(() => {
    return columns.map((col) => {
      const mrtCol = {
        id:
          col.field ||
          col.headerName?.replace(/\s+/g, "_") ||
          `col_${Math.random().toString(36).substr(2, 9)}`,
        header: col.headerName || col.field || "",
        size: col.width || 150,
        minSize: col.minWidth,
      };

      if (col.field) {
        mrtCol.accessorKey = col.field;
      }

      if (isLoading) {
        mrtCol.Cell = () => <Skeleton animation="wave" height={20} width="80%" />;
      } else {
        if (col.renderCell) {
          mrtCol.Cell = ({ row, cell }) => {
            // Provide the params format that DataGrid's renderCell expects
            const value = cell.getValue();
            return col.renderCell({
              row: row.original,
              value,
              id: row.id,
              field: col.field,
            });
          };
        }
        if (col.valueGetter) {
          mrtCol.accessorFn = (row) => col.valueGetter({ row });
        }
      }

      return mrtCol;
    });
  }, [columns, isLoading]);
  const handleExportData = (filename) => {
    const defaultName = `TableExport_${new Date().toISOString().split('T')[0]}.xlsx`;
    const finalFilename = filename || defaultName;
    
    if (!rows || rows.length === 0) return;

    // Use columns array to map row data to human-readable headers for export
    const exportableColumns = columns.filter(
      (col) =>
        col.field &&
        col.headerName &&
        col.headerName.toLowerCase() !== "action",
    );

    const exportData = rows.map((row) => {
      const rowData = {};
      exportableColumns.forEach((col) => {
        let val = col.valueGetter ? col.valueGetter({ row }) : row[col.field];
        rowData[col.headerName] = val;
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, finalFilename);
  };
  // Standardize initial state
  const processedInitialState = {
    pagination: { pageSize: 10, pageIndex: 0 },
    ...initialState,
  };

  if (initialState?.pagination?.paginationModel) {
    processedInitialState.pagination = {
      pageSize: initialState.pagination.paginationModel.pageSize || 10,
      pageIndex: initialState.pagination.paginationModel.page || 0,
    };
  }
  const skeletonData = useMemo(() => {
    return Array(10)
      .fill({})
      .map((_, i) => ({
        [rowIdField]: `skeleton-${i}`,
        id: `skeleton-${i}`,
      }));
  }, [rowIdField]);

  const table = useMaterialReactTable({
    columns: mrtColumns,
    data:
      isLoading && (!rows || rows.length === 0)
        ? skeletonData
        : rows,
    enableEditing: enableEditing,
    enableColumnOrdering: true,
    enableDensityToggle: true,
    enableGlobalFilter: true,
    enableFullScreenToggle: false,
    initialState: processedInitialState,
    state: {
      showSkeletons: isLoading,
      ...(rest.state || {}),
    },
    muiCircularProgressProps: {
      sx: { display: 'none' }
    },
    muiPaginationProps: {
      rowsPerPageOptions: pageSizeOptions,
    },
    getRowId:
      isLoading && (!rows || rows.length === 0)
        ? undefined
        : getRowId
          ? getRowId
          : (row) => row[rowIdField],
    renderTopToolbarCustomActions: renderTopToolbarCustomActions
      ? ({ table }) => (
           <Box
            sx={{
              display: "flex",
              gap: "1rem",
              p: 0,
              flexWrap: "wrap",
              alignItems: "center",

              width: "100%",
              minWidth: 0,

              "@media (max-width: 768px)": {
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                padding: 0,
                margin: 0,
              },
            }}
          >
            {renderTopToolbarCustomActions({ table })}
          </Box>
        )
      : undefined,

  muiTopToolbarProps: {
  sx: {
    minHeight: 56,
    px: 1.5,
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #e2e8f0",

    "& .MuiToolbar-root": {
      minHeight: "56px !important",
      alignItems: "center",
    },

    "& > .MuiBox-root": {
      alignItems: "center",
      p: 0.2,
    },

    // =========================
    // MOBILE
    // =========================
    "@media (max-width: 768px)": {
      minHeight: "auto",
      height: "auto",
      padding: "10px 14px",

      // IMPORTANT
      "& .MuiToolbar-root": {
        minHeight: "auto !important",
        width: "100%",
        padding: 0,

        // Don't use column here
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "flex-start",
      },

      // Every direct section inside MRT toolbar
      "& .MuiToolbar-root > *": {
        boxSizing: "border-box",
      },

      // =========================
      // FIRST ROW - TITLE
      // =========================
      "& .MuiToolbar-root > *:first-child": {
        flex: "0 0 100%",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      },

      // =========================
      // SECOND ROW - ACTIONS
      // =========================
      "& .MuiToolbar-root > *:last-child": {
        flex: "0 0 100%",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,

        marginTop: "8px",
        paddingTop: "6px",

        borderTop: "1px solid #f1f5f9",

        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
      },
    },
  },
},
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        fontFamily: '"Inter", sans-serif',
        "&, & *": {
          fontFamily: '"Inter", sans-serif !important',
        },
        ...(rest.muiTablePaperProps?.sx || {}),
      },
    },
    muiTableContainerProps: {
      sx: {
        minHeight: "350px",
        ...(rest.muiTableContainerProps?.sx || {}),
      },
      ...(rest.muiTableContainerProps || {}),
    },
   renderToolbarInternalActions: ({ table }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      pr: 0,

      "& .MuiIconButton-root": {
        color: "#64748B",
        borderRadius: 2,
        transition: "all 0.2s ease",

        "&:hover": {
          bgcolor: "#F1F5F9",
          color: "#2563EB",
        },
      },

      "& .MuiSvgIcon-root": {
        fontSize: "1.25rem",
      },

      "& .MuiIconButton-root:nth-of-type(4) .MuiSvgIcon-root": {
        fontSize: "1rem",
      },

      "@media (max-width: 768px)": {
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",

        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",

        flexWrap: "wrap",
        gap: 0.25,

        py: 0.5,

        "& .MuiIconButton-root": {
          flexShrink: 0,
        },
      },
    }}
  >
    <MRT_ToggleGlobalFilterButton table={table} />
    <MRT_ToggleFiltersButton table={table} />
    <MRT_ShowHideColumnsButton table={table} />
    <MRT_ToggleDensePaddingButton table={table} />

    {enableExport && (
      <ExportFilenamePopover defaultFilename={`TableExport_${new Date().toISOString().split('T')[0]}.xlsx`} onExport={handleExportData}>
        <Tooltip title="Export to Excel">
          <IconButton size="small">
            <Download size={18} />
          </IconButton>
        </Tooltip>
      </ExportFilenamePopover>
    )}

    {customInternalActions && customInternalActions({ table })}
  </Box>
),
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        whiteSpace: "nowrap",
        py: "8px",
        px: column.getIndex() === 0 ? "16px" : "3px",
        borderBottom: "1px solid #e2e8f0",
      },
    }),
    muiTableBodyCellProps: ({ cell }) => ({
      sx: {
        py: "8px",
        px: cell.column.getIndex() === 0 ? "16px" : "3px",
        borderBottom: "1px solid #e2e8f0",
      },
    }),
    ...rest,
  });
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ".MuiPaper-root": {
          boxShadow: "none",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <MaterialReactTable table={table} />
    </Box>
  );
}
